import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './survey.module.css';
import Header from '../../components/standartComponents/header';

const levelMap = {
  'pre-A1': { from: 'pre-A1', to: 'A1', lessons: 18 },
  'A1': { from: 'A1', to: 'A2', lessons: 18 },
  'A2': { from: 'A2', to: 'B1', lessons: 18 },
  'B1': { from: 'B1', to: 'B2', lessons: 18 },
  'B2': { from: 'B2', to: 'C1', lessons: 18 },
  'C1–C2': { from: 'C1', to: 'C2', lessons: 18 }
};

const Survey = ({ authUser }) => {
  const navigate = useNavigate();

  const totalSteps = 3;
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(['', '', '']);
  const [direction, setDirection] = useState(''); // 'forward' | 'backward'
  const [animating, setAnimating] = useState(false);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleSelect = (val) => {
    const newAnswers = [...answers];
    newAnswers[currentStep] = val;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setDirection('forward');
      setAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setAnimating(false);
      }, 300);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection('backward');
      setAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setAnimating(false);
      }, 300);
    }
  };

  const pct = currentStep < totalSteps ? ((currentStep + 1) / totalSteps) * 100 : 100;
  
  const getStepClass = (stepIndex) => {
    if (animating) {
      if (stepIndex === currentStep) {
        return direction === 'forward' ? css.exitLeft : css.exitRight;
      }
      if (direction === 'forward' && stepIndex === currentStep + 1) {
        return css.active; // Will animate in via active class animation
      }
      if (direction === 'backward' && stepIndex === currentStep - 1) {
        return css.enterFromLeft;
      }
    }
    return stepIndex === currentStep ? css.active : '';
  };

  return (
    <div className={css.surveyPage}>
      <div className={css.gridBg}></div>
      <Header authUser={authUser} />

      <main className={`${css.main} ${css.mainFadeIn}`}>
        
        {currentStep < totalSteps && (
          <>
            <div className={css.microTop}>
              <div className={css.microBadge}>
                <span className={css.microBadgeDot}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
                </span>
                Створення персонального плану
              </div>
              <div className={css.microSub}>Це займе менше 1 хвилини. Ваші відповіді допоможуть створити правильний план.</div>
            </div>

            <div className={css.progressWrap}>
              <div className={css.progressTop}>
                <span className={css.progressStep}>Крок {currentStep + 1} з {totalSteps}</span>
                <div className={css.progressDots}>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={`${css.progressDot} ${i < currentStep ? css.done : ''} ${i === currentStep ? css.active : ''}`}></div>
                  ))}
                </div>
              </div>
              <div className={css.progressBarWrap}>
                <div className={css.progressBarFill} style={{ width: `${pct}%` }}></div>
              </div>
            </div>
          </>
        )}

        {/* STEP 1: LANGUAGE */}
        <div className={`${css.stepView} ${getStepClass(0)}`}>
          <div className={css.stepContainer}>
            <div className={css.stepQuestion}>Якою мовою ви хочете переглядати сайт?</div>
            <div className={css.stepSubtitle}>Оберіть мову інтерфейсу для навчання.</div>
            <div className={`${css.optionsGrid} ${css.cols3}`}>
              {['Українська', 'English', 'Polski'].map((lang, idx) => {
                const isSelected = answers[0] === lang;
                const codes = ['UA', 'EN', 'PL'];
                return (
                  <div key={lang} className={`${css.optCard} ${css.langCard} ${isSelected ? css.selected : ''}`} onClick={() => handleSelect(lang)}>
                    <div className={css.optIcon}>{codes[idx]}</div>
                    <div className={css.optBody}><div className={css.optTitle}>{lang}</div></div>
                    <div className={css.optCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={css.navBar}>
            <button className={`${css.btnBack} ${css.hidden}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> Назад
            </button>
            <button className={css.btnNext} disabled={!answers[0]} onClick={handleNext}>
              Далі <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </button>
          </div>
        </div>

        {/* STEP 2: GOAL */}
        <div className={`${css.stepView} ${getStepClass(1)}`}>
          <div className={css.stepContainer}>
            <div className={css.stepQuestion}>Для чого вам англійська?</div>
            <div className={css.stepSubtitle}>Ми підберемо теми уроків під вашу ціль.</div>
            <div className={`${css.optionsGrid} ${css.cols1}`}>
              {[
                { title: 'Подорожі', desc: 'Аеропорт, готель, кафе, знайомства за кордоном.', icon: '✈️', color: 'rgba(245,158,11,0.08)' },
                { title: 'Робота', desc: 'Співбесіди, зустрічі, листування, робочі діалоги.', icon: '💼', color: 'rgba(99,102,241,0.08)' },
                { title: 'Навчання', desc: 'Освітні теми, академічна лексика, пояснення.', icon: '🎓', color: 'rgba(16,185,129,0.08)' },
                { title: 'Спілкування з людьми', desc: 'Small talk, знайомства, щоденні розмови.', icon: '💬', color: 'rgba(244,63,94,0.08)' },
                { title: 'Для себе / для розваги', desc: 'Відео, теми з життя, легка практика у власному темпі.', icon: '🎵', color: 'rgba(139,92,246,0.08)' },
              ].map(opt => {
                const isSelected = answers[1] === opt.title;
                return (
                  <div key={opt.title} className={`${css.optCard} ${isSelected ? css.selected : ''}`} onClick={() => handleSelect(opt.title)}>
                    <div className={css.optIcon} style={{ background: opt.color }}>{opt.icon}</div>
                    <div className={css.optBody}>
                      <div className={css.optTitle}>{opt.title}</div>
                      <div className={css.optDesc}>{opt.desc}</div>
                    </div>
                    <div className={css.optCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={css.navBar}>
            <button className={css.btnBack} onClick={handlePrev}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> Назад
            </button>
            <button className={css.btnNext} disabled={!answers[1]} onClick={handleNext}>
              Далі <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </button>
          </div>
        </div>

        {/* STEP 3: LEVEL */}
        <div className={`${css.stepView} ${getStepClass(2)}`}>
          <div className={css.stepContainer}>
            <div className={css.stepQuestion}>Який ваш поточний рівень англійської?</div>
            <div className={css.stepSubtitle}>Оберіть рівень, з якого почати персональний план.</div>
            <div className={`${css.optionsGrid} ${css.cols1}`}>
              {[
                { code: 'pre-A1', title: 'Починаю з нуля' },
                { code: 'A1', title: 'Знаю базові слова і прості фрази' },
                { code: 'A2', title: 'Розумію прості діалоги' },
                { code: 'B1', title: 'Можу говорити на знайомі теми' },
                { code: 'B2', title: 'Вільно спілкуюся у більшості ситуацій' },
                { code: 'C1–C2', title: 'Говорю впевнено на складні теми' },
              ].map(lvl => {
                const isSelected = answers[2] === lvl.code;
                return (
                  <div key={lvl.code} className={`${css.optCard} ${css.levelCard} ${isSelected ? css.selected : ''}`} onClick={() => handleSelect(lvl.code)}>
                    <span className={css.levelCode}>{lvl.code}</span>
                    <div className={css.optBody}><div className={css.optTitle}>{lvl.title}</div></div>
                    <div className={css.optCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                  </div>
                );
              })}
            </div>
            <div className={css.helperNote}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              Не впевнені? Оберіть приблизний рівень — його можна змінити пізніше.
            </div>
          </div>
          <div className={css.navBar}>
            <button className={css.btnBack} onClick={handlePrev}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg> Назад
            </button>
            <button className={css.btnNext} disabled={!answers[2]} onClick={handleNext}>
              Створити план <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
            </button>
          </div>
        </div>

        {/* RESULT */}
        <div className={`${css.stepView} ${getStepClass(3)}`}>
          <div className={css.resultContainer}>
            <div className={css.resultIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h2 className={css.resultTitle}>Ваш персональний план готовий</h2>
            <p className={css.resultSub}>Ми створили курс під вашу ціль і рівень.</p>

            <div className={css.planCard}>
              <div className={css.planRow}>
                <span className={css.planLabel}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m2 12 10 4 10-4"/></svg> Мова сайту
                </span>
                <span className={css.planValue}>{answers[0] || '—'}</span>
              </div>
              <div className={css.planRow}>
                <span className={css.planLabel}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/></svg> Ціль
                </span>
                <span className={css.planValue}>{answers[1] || '—'}</span>
              </div>
              <div className={css.planRow}>
                <span className={css.planLabel}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> Рівень
                </span>
                <span className={css.planValue}>{answers[2] || '—'}</span>
              </div>
              
              <div className={css.planHighlight}>
                <div className={css.planArrow}>
                  <span>{(levelMap[answers[2]] || levelMap['pre-A1']).from}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  <span>{(levelMap[answers[2]] || levelMap['pre-A1']).to}</span>
                </div>
                <span className={css.planLessons}>{(levelMap[answers[2]] || levelMap['pre-A1']).lessons} уроків</span>
              </div>
            </div>

            <div className={css.resultActions}>
              <button className={css.btnStart} onClick={() => navigate('/roadMap')}>
                Почати перший урок <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
              <button className={css.btnEdit} onClick={() => { setDirection('backward'); setCurrentStep(0); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                Змінити відповіді
              </button>
            </div>
            
            <p className={css.resultNote}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              Ви зможете змінити рівень або тему навчання пізніше в налаштуваннях плану.
            </p>
          </div>
        </div>

      </main>
    </div>
  );
};

export default Survey;